import { z } from 'zod';

/**
 * Schemas Zod du module Services (Phase 9 — US-SVC-01 à 09).
 *
 * Couvre :
 *  - CRUD service (US-SVC-02) — code, nom, type, statut, parent, capacité…
 *  - Hiérarchie parent/enfant (US-SVC-03) — self-FK + détection cycles applicative.
 *  - Responsables (US-SVC-04) — fonction multiple, titulaire unique par fonction.
 *  - Paramètres maintenance (US-SVC-05) — délais, fenêtres, jours interdits.
 *  - Budget (US-SVC-06) — exercice, ventilation, alertes seuils.
 *  - Localisations (US-SVC-09) — rattachement bâtiment/étage/salle.
 */

// ─── Constantes ─────────────────────────────────────────────────────────────

export const SERVICE_TYPES = [
  'operationnel',
  'technique',
  'logistique',
  'support',
  'administratif',
] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];

export const SERVICE_STATUTS = [
  'actif',
  'inactif',
  'en_restructuration',
  'ferme_temporairement',
  'desaffecte',
] as const;
export type ServiceStatut = (typeof SERVICE_STATUTS)[number];

export const SERVICE_RESPONSABLE_FONCTIONS = [
  'chef_service',
  'responsable_operationnel',
  'cadre_superieur',
  'referent_maintenance',
  'correspondant_gmao',
  'technicien_referent',
  'assistant_service',
] as const;
export type ServiceResponsableFonction = (typeof SERVICE_RESPONSABLE_FONCTIONS)[number];

export const SERVICE_LOCALISATION_ENTITY_TYPES = ['batiment', 'etage', 'salle'] as const;
export type ServiceLocalisationEntityType = (typeof SERVICE_LOCALISATION_ENTITY_TYPES)[number];

export const SERVICE_LOCALISATION_TYPES_OCCUPATION = [
  'principal',
  'secondaire',
  'partage',
] as const;
export type ServiceLocalisationTypeOccupation =
  (typeof SERVICE_LOCALISATION_TYPES_OCCUPATION)[number];

// ─── Service CRUD (US-SVC-02) ───────────────────────────────────────────────

export const createServiceSchema = z.object({
  code: z.string().min(1).max(50),
  nom_complet: z.string().min(1).max(255),
  type_service: z.enum(SERVICE_TYPES),
  statut: z.enum(SERVICE_STATUTS).default('actif'),
  service_parent_id: z.string().uuid().optional().nullable(),
  capacite_maximale_simultanee: z.coerce.number().int().min(0).optional().nullable(),
  nombre_postes_travail: z.coerce.number().int().min(0).optional().nullable(),
  surface_totale: z.coerce.number().min(0).optional().nullable(),
  autorisation_deplacement_equipement: z.boolean().default(true),
  validation_requise_deplacement: z.boolean().default(false),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
});
export type CreateServiceInput = z.infer<typeof createServiceSchema>;

export const updateServiceSchema = createServiceSchema.partial();
export type UpdateServiceInput = z.infer<typeof updateServiceSchema>;

export const listServicesQuerySchema = z.object({
  search: z.string().trim().max(255).optional(),
  type_service: z.enum(SERVICE_TYPES).optional(),
  statut: z.enum(SERVICE_STATUTS).optional(),
  service_parent_id: z.string().uuid().optional(),
  /** Si true, ne retourne que les services racines (sans parent). */
  root_only: z.coerce.boolean().optional(),
  sort_by: z
    .enum(['code', 'nom_complet', 'type_service', 'statut', 'created_at'])
    .default('nom_complet'),
  sort_order: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListServicesQuery = z.infer<typeof listServicesQuerySchema>;

// ─── Responsables (US-SVC-04) ───────────────────────────────────────────────

export const createServiceResponsableSchema = z.object({
  service_id: z.string().uuid(),
  utilisateur_id: z.string().uuid(),
  fonction: z.enum(SERVICE_RESPONSABLE_FONCTIONS),
  est_titulaire: z.boolean().default(true),
  date_debut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Format date attendu YYYY-MM-DD'),
  date_fin: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Format date attendu YYYY-MM-DD')
    .optional()
    .nullable(),
});
export type CreateServiceResponsableInput = z.infer<typeof createServiceResponsableSchema>;

export const updateServiceResponsableSchema = createServiceResponsableSchema
  .omit({ service_id: true })
  .partial();
export type UpdateServiceResponsableInput = z.infer<typeof updateServiceResponsableSchema>;

// ─── Paramètres maintenance (US-SVC-05) ─────────────────────────────────────

const fenetreInterventionSchema = z.object({
  /** 0 = dimanche, 1 = lundi, ..., 6 = samedi (JS-style). */
  jour: z.number().int().min(0).max(6),
  /** Format `HH:MM`. */
  debut: z.string().regex(/^\d{2}:\d{2}$/),
  fin: z.string().regex(/^\d{2}:\d{2}$/),
});
export type FenetreIntervention = z.infer<typeof fenetreInterventionSchema>;

const periodeInterdictionSchema = z.object({
  date_debut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  date_fin: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  motif: z.string().max(255).optional(),
});
export type PeriodeInterdiction = z.infer<typeof periodeInterdictionSchema>;

export const upsertServiceParametresMaintenanceSchema = z.object({
  delai_critique: z.coerce.number().int().min(0).default(1),
  delai_urgence: z.coerce.number().int().min(0).default(8),
  delai_prioritaire: z.coerce.number().int().min(0).default(24),
  delai_normal: z.coerce.number().int().min(0).default(72),
  fenetres_intervention: z.array(fenetreInterventionSchema).optional().nullable(),
  periodes_interdiction: z.array(periodeInterdictionSchema).optional().nullable(),
  jours_interdits: z.array(z.number().int().min(0).max(6)).optional().nullable(),
  preavis_minimum: z.coerce.number().int().min(0).default(0),
  duree_maximale_intervention: z.coerce.number().int().min(0).optional().nullable(),
  contraintes_acces: z
    .object({
      accompagnement_obligatoire: z.boolean().optional(),
      badge_securise_requis: z.boolean().optional(),
      formation_specifique: z.array(z.string()).optional(),
      autre: z.string().max(1000).optional(),
    })
    .partial()
    .optional()
    .nullable(),
});
export type UpsertServiceParametresMaintenanceInput = z.infer<
  typeof upsertServiceParametresMaintenanceSchema
>;

// ─── Budget (US-SVC-06) ─────────────────────────────────────────────────────

export const upsertServiceBudgetSchema = z.object({
  exercice: z.string().regex(/^\d{4}$/, 'Exercice attendu format YYYY'),
  budget_alloue: z.coerce.number().min(0).default(0),
  budget_preventif: z.coerce.number().min(0).default(0),
  budget_curatif: z.coerce.number().min(0).default(0),
  budget_amelioratif: z.coerce.number().min(0).default(0),
  budget_consomme: z.coerce.number().min(0).default(0),
  budget_engage: z.coerce.number().min(0).default(0),
  centre_cout: z.string().max(50).optional().nullable(),
});
export type UpsertServiceBudgetInput = z.infer<typeof upsertServiceBudgetSchema>;

// ─── Localisations (US-SVC-09) ──────────────────────────────────────────────

export const createServiceLocalisationSchema = z.object({
  entity_type: z.enum(SERVICE_LOCALISATION_ENTITY_TYPES),
  entity_id: z.string().uuid(),
  type_occupation: z.enum(SERVICE_LOCALISATION_TYPES_OCCUPATION).default('principal'),
  date_debut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  date_fin: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional()
    .nullable(),
});
export type CreateServiceLocalisationInput = z.infer<typeof createServiceLocalisationSchema>;
